/**
 * 应用配置管理
 * 客户端可读变量使用 NEXT_PUBLIC_ 前缀（Vite envPrefix 与既有 .env 兼容）
 */

/**
 * 应用环境类型
 */
export type AppEnvironment = "development" | "staging" | "production";

/**
 * 获取当前环境
 */
export const getEnvironment = (): AppEnvironment => {
  const mode = import.meta.env.NEXT_PUBLIC_APP_MODE?.trim();
  if (mode === "production" || mode === "staging" || mode === "development") {
    return mode;
  }
  return import.meta.env.PROD ? "production" : "development";
};

/* ------------------------------------------------------------------ */
/* 子应用 SSO 配置                                                     */
/* ------------------------------------------------------------------ */

/**
 * 子应用 SSO 跳转模式
 */
export type SsoMode = "shared" | "oauth2";

/**
 * 子应用配置
 */
export interface SubAppConfig {
  /** OAuth2 clientId，用于 /oauth2/authorize 接口 */
  clientId: string;
  /** 子应用首页地址（末尾带 /），登录成功后回跳目标 */
  baseUrl: string;
  /** SSO 跳转模式：shared=同域 cookie 共享直接跳；oauth2=携带授权码跳转 */
  ssoMode: SsoMode;
}

/**
 * 通用子应用 baseUrl 解析器
 *
 * 优先级：环境变量 > 按 MODE 默认值。
 * 新增子应用时只需调用此函数，无需复制粘贴。
 */
function resolveSubAppBaseUrl(
  envKey: string,
  defaults: { dev: string; staging: string; prod: string }
): string {
  const fromEnv = (import.meta.env[envKey] as string | undefined)?.trim();
  if (fromEnv) {
    return fromEnv.endsWith("/") ? fromEnv : `${fromEnv}/`;
  }
  if (import.meta.env.NEXT_PUBLIC_APP_MODE?.trim() === "staging") {
    return defaults.staging;
  }
  if (import.meta.env.PROD) {
    return defaults.prod;
  }
  return defaults.dev;
}

/**
 * 解析子应用 SSO 模式环境变量，未配置时使用传入的默认值
 */
function resolveSsoMode(envKey: string, defaultValue: SsoMode): SsoMode {
  return (import.meta.env[envKey] as SsoMode) || defaultValue;
}

const toolboxBaseUrl = resolveSubAppBaseUrl("NEXT_PUBLIC_TOOLBOX_BASE_URL", {
  dev: "https://test.toolbox.oran.cn/",
  staging: "https://test.toolbox.oran.cn/",
  prod: "https://toolbox.oran.cn/",
});

const promptBaseUrl = resolveSubAppBaseUrl("NEXT_PUBLIC_PROMPT_BASE_URL", {
  dev: "https://oranaiprompt.photog.art/",
  staging: "https://oranaiprompt.photog.art/",
  prod: "https://oranaiprompt.oran.cn/",
});

/**
 * 子应用注册表
 *
 * key 即 ?login=<key> 中的标识符，也即 sessionStorage.loginRedirect 的值。
 * 新增子应用时在此注册即可，无需改动其他文件的核心逻辑。
 */
const subApps: Record<string, SubAppConfig> = {
  toolbox: {
    clientId: "portal-a",
    baseUrl: toolboxBaseUrl,
    ssoMode: resolveSsoMode("NEXT_PUBLIC_TOOLBOX_SSO_MODE", "shared"),
  },
  prompt: {
    clientId: "portal-prompt",
    baseUrl: promptBaseUrl,
    ssoMode: resolveSsoMode("NEXT_PUBLIC_PROMPT_SSO_MODE", "oauth2"),
  },
};

/**
 * 判断某个 key 是否为已注册的子应用标识
 *
 * 使用 hasOwnProperty 而非 `in` 运算符，避免 "toString"、"constructor" 等
 * Object.prototype 属性被误判为合法子应用标识。
 */
export function isSubAppKey(key: string): boolean {
  return Object.prototype.hasOwnProperty.call(subApps, key);
}

/* ------------------------------------------------------------------ */
/* 应用配置                                                            */
/* ------------------------------------------------------------------ */

/**
 * 应用配置
 */
export const config = {
  // 环境信息
  env: getEnvironment(),
  isDev: import.meta.env.DEV,
  isProd: import.meta.env.PROD,

  /**
   * 子应用注册表，用于 SSO 跳转。
   *
   * 每个子应用包含：
   * - clientId：OAuth2 授权码接口的客户端标识
   * - baseUrl：登录成功后回跳的目标地址
   * - ssoMode：跳转模式（shared / oauth2）
   *
   * 通过 isSubAppKey() 判断 ?login=xxx 是否为合法子应用标识。
   */
  subApps,

  // API 配置
  api: {
    baseUrl: import.meta.env.NEXT_PUBLIC_API_BASE_URL || "",
    timeout: Number(import.meta.env.NEXT_PUBLIC_API_TIMEOUT) || 30000,
    modelsBaseUrl: import.meta.env.NEXT_PUBLIC_MODELS_API_BASE_URL || "",
    libraryBaseUrl: import.meta.env.NEXT_PUBLIC_LIBRARY_API_BASE_URL || "",
    authBaseUrl: import.meta.env.NEXT_PUBLIC_AUTH_API_BASE_URL || "",
    /** 计费网关；未配置时与 auth 同源，仍为空则走站点相对路径 /billing（依赖反向代理） */
    billingBaseUrl: (
      import.meta.env.NEXT_PUBLIC_BILLING_API_BASE_URL ||
      import.meta.env.NEXT_PUBLIC_AUTH_API_BASE_URL ||
      ""
    ).replace(/\/$/, ""),
    imageGenUrl:
      import.meta.env.NEXT_PUBLIC_IMAGE_GEN_URL || `${toolboxBaseUrl}ai-toolbox/text-to-image`,
    videoGenUrl:
      import.meta.env.NEXT_PUBLIC_VIDEO_GEN_URL || `${toolboxBaseUrl}ai-toolbox/text-to-video`,
  },

  // 应用信息
  app: {
    name: import.meta.env.NEXT_PUBLIC_APP_NAME || "OranAI Vision Engine",
    version: import.meta.env.NEXT_PUBLIC_APP_VERSION || "1.0.0",
  },

  // 功能开关
  features: {
    enableAnalytics: import.meta.env.NEXT_PUBLIC_ENABLE_ANALYTICS === "true",
    enableErrorReporting: import.meta.env.NEXT_PUBLIC_ENABLE_ERROR_REPORTING !== "false",
    enablePerformanceMonitoring:
      import.meta.env.NEXT_PUBLIC_ENABLE_PERFORMANCE_MONITORING === "true",
  },

  // 第三方服务配置
  services: {
    sentry: {
      dsn: import.meta.env.NEXT_PUBLIC_SENTRY_DSN || "",
      enabled: import.meta.env.NEXT_PUBLIC_SENTRY_ENABLED === "true",
    },
    analytics: {
      id: import.meta.env.NEXT_PUBLIC_ANALYTICS_ID || "",
      enabled: import.meta.env.NEXT_PUBLIC_ANALYTICS_ENABLED === "true",
    },
  },
} as const;

/**
 * 验证必需的配置项
 */
export const validateConfig = (): void => {
  const requiredEnvVars: string[] = [];

  if (config.isProd) {
    // 生产环境必需的配置
  }

  if (requiredEnvVars.length > 0) {
    const env = import.meta.env as Record<string, string | boolean | undefined>;
    const missing = requiredEnvVars.filter((key) => !env[key]);
    if (missing.length > 0) {
      console.warn(`缺少以下环境变量: ${missing.join(", ")}. 应用可能无法正常工作.`);
    }
  }
};

if (typeof window !== "undefined") {
  validateConfig();
}
